import {useMemo} from 'react';
import {Control, Controller, ControllerProps, useWatch, ValidationRule} from 'react-hook-form';
import AppAutoComplete, {AppAutoCompleteProps} from './AppAutoComplete';
import {SelectOptionsType} from 'global';

export type AppControlledAutoCompleteProps = Omit<AppAutoCompleteProps<SelectOptionsType>, 'name' | 'required'> &
    Omit<ControllerProps, 'render' | 'control'> & {
        required?: string | ValidationRule<boolean>;
        control: Control<any, any, any>;
        shouldReturnValueOnly?: boolean;
    };

const EMPTY_OPTION: SelectOptionsType = {
    label: '',
    value: '',
    color: '',
    textColor: '',
};

function getStringValue(val: any): string {
    return typeof val === 'object' && val !== null && 'value' in val ? val.value : val || '';
}

export function AppControlledAutoComplete({
    name,
    control,
    rules,
    required,
    shouldReturnValueOnly = false,
    ...rest
}: AppControlledAutoCompleteProps) {
    const watchedValue = useWatch({control, name});

    const selectedValue = useMemo(() => {
        if (shouldReturnValueOnly) {
            return rest.options?.find(opt => opt.value === watchedValue) || '';
        } else {
            return watchedValue || EMPTY_OPTION;
        }
    }, [watchedValue, rest.options, shouldReturnValueOnly]);

    return (
        <Controller
            name={name}
            rules={{required, ...rules}}
            control={control}
            render={({field, fieldState: {invalid, error}}) => (
                <AppAutoComplete
                    {...field}
                    groupBy={rest.groupBy}
                    disabled={rest.disabled || false}
                    onInputChange={rest.onInputChange}
                    disableClearable={rest.disableClearable || false}
                    options={rest.options || []}
                    loading={rest.loading || false}
                    label={rest.label}
                    descriptionText={rest.descriptionText}
                    fullWidth={rest.fullWidth || false}
                    size={rest.size || 'medium'}
                    onScrollBottomHandler={rest.onScrollBottomHandler}
                    required={!!required}
                    value={selectedValue}
                    onChange={(_e, _value) => field.onChange(shouldReturnValueOnly ? getStringValue(_value) : _value)}
                    isInvalid={invalid}
                    validationMessage={error?.type === 'required' ? `${rest.label} is required` : error?.message || ''}
                />
            )}
        />
    );
}

export default AppControlledAutoComplete;
